import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<boolean>>} */
  static #pendingQuantityUpdates = new Set();

  /** The number of Check out submits that wait for quantity saves, across all cart instances. */
  static #checkoutHolds = 0;

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * Each promise resolves to true when the store rejected the quantity and the line shows the error.
   * @returns {ReadonlyArray<Promise<boolean>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   * @param {(html: string) => boolean} [options.shouldRender] - Return false to keep the current section.
   */
  async #refreshCartSection(options = {}) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to refresh cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Re-renders the cart section when the page is restored from the back/forward cache.
   *
   * A restored page replays a frozen DOM, so the line items still show whatever the cart
   * held before the shopper navigated away. `cart-icon.js` self-corrects its count bubble
   * on this same event, which is why the bubble and the cart body disagree until a reload.
   *
   * `persisted` alone does not cover Chrome. Chrome refuses the bfcache for any document holding
   * the Sign in with Shop iframe, `/services/login_with_shop/authorize` inside the shadow root of
   * `shopify-account > shop-login`, and every product page carries one. A shopper's Back there
   * rebuilds the document from the HTTP disk cache instead: `persisted` is false and the frozen
   * cart markup renders anyway. Only the navigation entry separates that from an ordinary page
   * view, so it is the second channel. Safari and Firefox do restore the same page and arrive
   * through `persisted`. Measured on os3 across all three browsers, 2026-09-03.
   *
   * Both channels are still needed. A restored document keeps its ORIGINAL navigation entry in
   * Chrome and Safari, reporting `navigate`, so `back_forward` alone would miss every real
   * restore. And `pageshow` fires after `load` on every ordinary navigation, so dropping both
   * guards would put a section fetch on every page view.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    // `section_id` is optional in `snippets/cart-items-component.liquid` and the `sectionId`
    // getter throws without it. Every other caller reads it inside a promise chain that
    // already has a catch; this one runs straight off a window listener, so it would escape.
    if (!this.dataset.sectionId) return;

    // `cache: false` on both paths. A bfcache restore never fires `load`, so the renderer's cache
    // still holds the pre-navigation copy. A disk-cache Back does fire `load` and seeds the cache
    // from the stale document it just rebuilt. Either way the cached copy is the same stale HTML
    // the DOM already shows.
    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        // Empty → non-empty adds the cart summary markup, which carries its own stylesheet.
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        // Morph swaps the quantity selectors in place, so they never reconnect and their
        // min/max button states stay bound to the pre-restore quantities. A line restored to its
        // minimum would otherwise keep minus enabled: a wrong affordance and a wasted cart mutation.
        this.#updateCartQuantitySelectorButtonStates();
        // No drawer-open event fires on a restore, so the sticky summary measurement has to
        // be driven from here. `cart-drawer-component` listens for this.
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] bfcache restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    let rejected = false;
    let completeQuantityUpdate = () => {};
    /** @type {Promise<boolean>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve(rejected);
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);

    try {
      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        rejected = true;
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        // While Check out waits, a failed edit keeps the typed quantity, so the resumed form post carries it.
        const failedEdit = !response.ok && quantity > 0;
        await this.#refreshCartSection({ shouldRender: () => !failedEdit || !CartItemsComponent.#checkoutHolds });
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      deferredUpdatePromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Failed to update cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      try {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  /**
   * Check out waits for a pending quantity save. A quantity edit saves through Ajax after a short wait. A form post
   * at the same time makes iOS Safari cancel the save, and the store answers the post with no page and no message.
   * This flushes the wait, cancels the post, and awaits the save. A rejected quantity shows its error on the line
   * (updateQuantity), and the buyer stays on the cart. Any other result resumes the post. A failed save keeps the
   * typed quantity in its field (updateQuantity), so the store checks it on the post.
   * The wait saves only the latest edit. With unsaved edits on two or more lines, the post goes at once, as before,
   * so that it carries every typed quantity.
   * While Check out waits, this cancels a new submit, so the wait resumes the post once. After a rejection, a keyboard
   * submit gets the focus back in its field.
   * @param {SubmitEvent} event - The submit event on the cart form.
   */
  holdCheckoutForQuantitySave = async (event) => {
    const { submitter } = event;
    if (!(event.target instanceof HTMLFormElement) || !(submitter instanceof HTMLElement)) return;
    if (event.defaultPrevented) return;
    // The wait in progress resumes the post. Cancel this submit, so that only one post goes out.
    if (CartItemsComponent.#checkoutHolds) {
      event.preventDefault();
      return;
    }

    let edited = 0;
    for (const input of event.target.querySelectorAll('input[name="updates[]"]')) {
      if (input instanceof HTMLInputElement && input.value !== input.defaultValue) edited++;
    }
    if (edited > 1) return;

    // A keyboard submit leaves the focus in an edited field. The blur queues its save.
    const active = document.activeElement;
    const blurred =
      active instanceof HTMLInputElement && event.target.contains(active) && active.value !== active.defaultValue
        ? active
        : null;
    blurred?.blur();
    this.#debouncedOnChange.flush();

    let pending = CartItemsComponent.pendingQuantityUpdates;
    if (!pending.length) return;
    event.preventDefault();

    let rejected = false;
    CartItemsComponent.#checkoutHolds++;
    try {
      while (pending.length) {
        rejected = (await Promise.all(pending)).some(Boolean) || rejected;
        this.#debouncedOnChange.flush();
        pending = CartItemsComponent.pendingQuantityUpdates;
      }
    } finally {
      CartItemsComponent.#checkoutHolds--;
    }
    if (rejected) {
      // The blur moved the focus to the page. Put it back in the field, unless the buyer moved it during the wait.
      if (document.activeElement === document.body) blurred?.focus();
      return;
    }

    // The save re-rendered the section, so the submitter can be a detached element.
    const button = submitter.isConnected ? submitter : document.getElementById(submitter.id);
    button?.click();
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection(morphOptions);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    // Discounts such as Buy X Get Y can split one variant across several cart lines.
    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * `getEntriesByType` is typed as the `PerformanceEntry` base, which has no `type`, and the DOM
   * lib has no per-string overload for `'navigation'`. `entryType` is the spec's discriminant and
   * lives on the base, so this narrows at runtime instead of asserting the way a cast would.
   *
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/**
 * Edits a cart line through the existing cart sections and event contracts.
 * @typedef {object} DwCartEditRefs
 * @property {HTMLDetailsElement} panel
 * @property {HTMLSelectElement} color
 * @property {HTMLSelectElement} variant
 * @property {HTMLButtonElement} apply
 * @property {HTMLElement} price
 * @property {HTMLElement} status
 * @property {HTMLScriptElement} data
 * @extends Component<DwCartEditRefs>
 */
class DwCartVariantEdit extends Component {
  requiredRefs = ['panel', 'color', 'variant', 'apply', 'price', 'status', 'data'];
  static busy = false;
  #products = [];
  #initialOptions = [];
  #busy = false;
  #reads;
  #initialFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleInitialization();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleInitialization();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reads?.abort();
    if (this.#initialFrame) cancelAnimationFrame(this.#initialFrame);
  }

  #scheduleInitialization() {
    if (this.#initialFrame) cancelAnimationFrame(this.#initialFrame);
    this.#initialFrame = requestAnimationFrame(() => {
      this.#initialFrame = 0;
      if (!this.isConnected) return;
      super.updatedCallback();
      this.#initialize();
    });
  }

  #initialize() {
    this.#reads?.abort();
    this.#reads = new AbortController();
    try {
      const data = JSON.parse(this.refs.data.textContent || '{}');
      this.#products = [data.current, ...(data.siblings || [])].filter(Boolean);
      const current = this.#products.find(product => String(product.id) === this.dataset.currentProduct);
      this.#initialOptions = current?.variants.find(variant => String(variant.id) === this.dataset.variantId)?.options || [];
      this.setAttribute('data-ready', '');
      this.#updatePrice();
    } catch {
      this.removeAttribute('data-ready');
    }
  }

  openEditor() {
    super.updatedCallback();
    this.#initialize();
  }

  #product() {
    return this.#products.find(product => String(product.id) === this.refs.color.value);
  }

  #variant() {
    return this.#product()?.variants.find(variant => String(variant.id) === this.refs.variant.value);
  }

  onColorChange() {
    const product = this.#product();
    if (!product) return;
    const current = this.#products.find(item => String(item.id) === this.dataset.currentProduct);
    const sizeIndex = current?.options.findIndex(name => /^(taglia|size|misura|taille|größe|talla)$/i.test(name)) ?? -1;
    const targetSizeIndex = product.options.findIndex(name => /^(taglia|size|misura|taille|größe|talla)$/i.test(name));
    const size = sizeIndex >= 0 ? this.#initialOptions[sizeIndex] : null;
    let selected = product.variants.find(variant => variant.available && size != null && targetSizeIndex >= 0 && variant.options[targetSizeIndex] === size);
    if (!selected && product.variants.length === 1 && product.variants[0].available) selected = product.variants[0];
    const options = [];
    if (!selected) options.push(new Option(this.refs.variant.dataset.placeholder || '', ''));
    for (const variant of product.variants) {
      const label = variant.title + (variant.available ? '' : ' — ' + this.refs.variant.dataset.soldOut);
      const option = new Option(label, String(variant.id), false, variant.id === selected?.id);
      option.disabled = !variant.available;
      options.push(option);
    }
    this.refs.variant.replaceChildren(...options);
    this.refs.status.textContent = '';
    this.#updatePrice();
  }

  onVariantChange() {
    this.refs.status.textContent = '';
    this.#updatePrice();
  }

  #updatePrice() {
    const variant = this.#variant();
    this.refs.price.textContent = variant?.price || '—';
    this.refs.apply.disabled = this.#busy || !variant?.available || String(variant.id) === this.dataset.variantId;
  }

  cancelChange() {
    if (this.#busy) return;
    this.refs.color.value = this.dataset.currentProduct || '';
    this.onColorChange();
    this.refs.variant.value = this.dataset.variantId || '';
    this.#updatePrice();
    this.refs.panel.open = false;
    this.refs.panel.querySelector('summary')?.focus();
  }

  #properties(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
  }

  #matches(item, id, properties) {
    return String(item.variant_id) === String(id) && !item.selling_plan_allocation && !item.parent_relationship && this.#properties(item.properties) === this.#properties(properties);
  }

  async #readCart() {
    const response = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.#reads?.signal });
    if (!response.ok) throw new Error(this.dataset.error);
    return response.json();
  }

  async #post(url, body) {
    const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
    const data = await response.json();
    return { ok: response.ok && !data.errors && !data.status, data };
  }

  async #restoreTarget(id, properties, quantity) {
    for (let attempt = 0; attempt < 5; attempt++) {
      const cart = await this.#readCart();
      const lines = cart.items.filter(item => this.#matches(item, id, properties));
      const total = lines.reduce((sum, item) => sum + item.quantity, 0);
      if (total <= quantity) return cart;
      const line = lines[lines.length - 1];
      const result = await this.#post(Theme.routes.cart_change_url, { id: line.key, quantity: Math.max(0, line.quantity - (total - quantity)) });
      if (!result.ok) throw new Error(this.dataset.error);
    }
    throw new Error(this.dataset.error);
  }

  async #refresh(cart, owner, sections, variantId, message) {
    if (!owner?.isConnected) return;
    const sectionId = owner.dataset.sectionId;
    if (sectionId && sections?.[sectionId]) await morphSection(sectionId, sections[sectionId]);
    else if (sectionId) await sectionRenderer.renderSection(sectionId, { cache: false });
    const cartUpdate = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({action:'update', context:'cart', lines:[], promise:cartUpdate.promise}));
    cartUpdate.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{source:'cart-items-component',itemCount:cart.item_count,items:cart.items,sections,didError:false}});
    const currentOwner = document.querySelector('cart-items-component[data-section-id="' + CSS.escape(sectionId || '') + '"]');
    const editor = currentOwner?.querySelector('dw-cart-variant-edit[data-variant-id="' + String(variantId) + '"]');
    if (editor instanceof DwCartVariantEdit) editor.openEditor();
    const status = editor?.querySelector('[ref="status"]');
    if (status) status.textContent = message;
    editor?.querySelector('summary')?.focus({ preventScroll: true });
  }

  async applyChange() {
    const variant = this.#variant();
    if (this.#busy || DwCartVariantEdit.busy || !variant?.available || String(variant.id) === this.dataset.variantId) return;
    this.#busy = true;
    DwCartVariantEdit.busy = true;
    this.refs.status.textContent = this.dataset.pending || '';
    this.#updatePrice();
    const owner = this.closest('cart-items-component');
    const locks = Array.from(document.querySelectorAll('cart-items-component')).map(element => ({ element, inert: element.inert }));
    const checkout = Array.from(document.querySelectorAll('[name="checkout"]')).filter(element => element instanceof HTMLButtonElement || element instanceof HTMLInputElement).map(element => ({ element, disabled: element.disabled }));
    for (const lock of locks) lock.element.inert = true;
    for (const lock of checkout) lock.element.disabled = true;
    let original;
    let targetBefore = 0;
    let mutationStarted = false;
    let committed = false;
    const sections = Array.from(new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map(element => element.dataset.sectionId))).slice(0, 5);
    try {
      const before = await this.#readCart();
      original = before.items.find(item => item.key === this.dataset.key);
      if (!original || original.selling_plan_allocation || original.parent_relationship || original.has_components) throw new Error(this.dataset.changed);
      const originalLines = before.items.filter(item => this.#matches(item, original.variant_id, original.properties));
      if (originalLines.length !== 1) throw new Error(this.dataset.changed);
      targetBefore = before.items.filter(item => this.#matches(item, variant.id, original.properties)).reduce((sum, item) => sum + item.quantity, 0);
      mutationStarted = true;
      const added = await this.#post(Theme.routes.cart_add_url, { items: [{ id: variant.id, quantity: original.quantity, properties: original.properties || {} }] });
      const afterAdd = await this.#readCart();
      const targetTotal = afterAdd.items.filter(item => this.#matches(item, variant.id, original.properties)).reduce((sum, item) => sum + item.quantity, 0);
      if (!added.ok || targetTotal !== targetBefore + original.quantity) throw new Error(this.dataset.unavailable);
      const oldLines = afterAdd.items.filter(item => this.#matches(item, original.variant_id, original.properties));
      if (oldLines.length !== 1 || oldLines[0].quantity !== original.quantity) throw new Error(this.dataset.changed);
      const removed = await this.#post(Theme.routes.cart_change_url, { id: oldLines[0].key, quantity: 0, sections, sections_url: window.location.pathname });
      const finalCart = await this.#readCart();
      const oldRemaining = finalCart.items.filter(item => this.#matches(item, original.variant_id, original.properties)).reduce((sum, item) => sum + item.quantity, 0);
      if (!removed.ok || oldRemaining !== 0) throw new Error(this.dataset.error);
      committed = true;
      await this.#refresh(finalCart, owner, removed.data.sections, variant.id, this.dataset.success || '');
    } catch (error) {
      let message = error instanceof Error ? error.message : this.dataset.error || '';
      if (mutationStarted && original && !committed) {
        try {
          const cart = await this.#readCart();
          const oldTotal = cart.items.filter(item => this.#matches(item, original.variant_id, original.properties)).reduce((sum, item) => sum + item.quantity, 0);
          const targetTotal = cart.items.filter(item => this.#matches(item, variant.id, original.properties)).reduce((sum, item) => sum + item.quantity, 0);
          if (oldTotal === 0 && targetTotal === targetBefore + original.quantity) {
            committed = true;
            await this.#refresh(cart, owner, null, variant.id, this.dataset.success || '');
          } else if (oldTotal === original.quantity) {
            const restored = await this.#restoreTarget(variant.id, original.properties, targetBefore);
            await this.#refresh(restored, owner, null, original.variant_id, message);
          } else {
            await this.#refresh(cart, owner, null, original.variant_id, this.dataset.error || '');
          }
        } catch {
          if (this.isConnected) this.refs.status.textContent = this.dataset.error || '';
        }
      } else if (!committed && this.isConnected) {
        this.refs.status.textContent = message;
      }
    } finally {
      for (const lock of locks) lock.element.inert = lock.inert;
      for (const lock of checkout) if (lock.element.isConnected) lock.element.disabled = lock.disabled;
      this.#busy = false;
      DwCartVariantEdit.busy = false;
      if (this.isConnected) this.#updatePrice();
    }
  }
}

if (!customElements.get('dw-cart-variant-edit')) customElements.define('dw-cart-variant-edit', DwCartVariantEdit);
